// src/widgets/ScoreByRoundWidget.tsx
import { TurnScore } from "../index";

interface BustEntry {
  player: 0 | 1;
  round: number;
}

interface ScoreByRoundData {
  turnScoreHistory: TurnScore[];
  player1Name: string;
  player2Name: string;
  currentScores: [number, number];
  phase: 'normal' | 'over' | 'retribution' | 'sudden_death';
  currentRound: number;
  bustHistory?: BustEntry[];
}

/**
 * A widget to display scores by round, similar to baseball box scores.
 * Shows points scored per round (not cumulative) with totals at bottom.
 * Displays live updates for the current turn in progress.
 */
export function ScoreByRoundWidget(data?: ScoreByRoundData): HTMLElement {
  const content = document.createElement('div');
  content.className = 'widget-content score-by-round-widget';

  if (!data) {
    content.innerHTML = '<div class="no-data">Waiting for game...</div>';
    return content;
  }

  const { turnScoreHistory, player1Name, player2Name, currentScores, phase, currentRound, bustHistory = [] } = data;

  // Build round-by-round data structure
  // Each round has both player 0 and player 1's scores
  interface RoundData {
    round: number;
    suddenDeathRound?: number;
    player0Score: number | null;
    player1Score: number | null;
    isInProgress?: boolean; // Current round being played
  }

  const roundMap = new Map<string, RoundData>();

  // Build bust lookup per player
  const bustByPlayer: Record<0 | 1, Set<number>> = { 0: new Set(), 1: new Set() };
  const bustCounts: Record<0 | 1, number> = { 0: 0, 1: 0 };
  bustHistory.forEach(entry => {
    bustByPlayer[entry.player].add(entry.round);
    bustCounts[entry.player] += 1;
  });

  // Add completed turns from history
  turnScoreHistory.forEach(turn => {
    const key = turn.suddenDeathRound !== undefined
      ? `SD${turn.suddenDeathRound}`
      : `R${turn.round}`;

    if (!roundMap.has(key)) {
      roundMap.set(key, {
        round: turn.round,
        suddenDeathRound: turn.suddenDeathRound,
        player0Score: null,
        player1Score: null
      });
    }

    const roundData = roundMap.get(key)!;
    if (turn.player === 0) {
      roundData.player0Score = turn.points;
    } else {
      roundData.player1Score = turn.points;
    }
  });

  // Add current round in progress (label only). Scores for in-progress rounds
  // are derived from roll history via turnScoreHistory; we do not use turnHits.
  const currentKey = `R${currentRound}`;
  if (!roundMap.has(currentKey)) {
    roundMap.set(currentKey, {
      round: currentRound,
      player0Score: null,
      player1Score: null,
      isInProgress: true
    });
  }

  // Mark the current round as in-progress for styling, but do not mutate
  // scores here. Partial turn scores are already represented in
  // turnScoreHistory based on roll_history.
  const currentRoundData = roundMap.get(currentKey)!;
  currentRoundData.isInProgress = true;

  // Convert to sorted array
  const rounds = Array.from(roundMap.entries())
    .sort((a, b) => {
      const [keyA, dataA] = a;
      const [keyB, dataB] = b;

      // Regular rounds come before sudden death
      const isSDa = keyA.startsWith('SD');
      const isSDb = keyB.startsWith('SD');

      if (isSDa && !isSDb) return 1;
      if (!isSDa && isSDb) return -1;

      // Both same type - sort by round number
      if (isSDa && isSDb) {
        return (dataA.suddenDeathRound || 0) - (dataB.suddenDeathRound || 0);
      }
      return dataA.round - dataB.round;
    })
    .map(([key, data]) => ({ key, ...data }));

  // Build table HTML
  let tableHTML = `
    <table class="score-table">
      <thead>
        <tr>
          <th></th>
          <th>P1</th>
          <th>P2</th>
        </tr>
      </thead>
      <tbody>
  `;

  rounds.forEach(round => {
    const label = round.suddenDeathRound !== undefined
      ? `SD${round.suddenDeathRound}`
      : `R${round.round}`;

    // For in-progress rounds, show 0 instead of "-"
    // For completed rounds, show "-" if that player hasn't gone yet
    const p0Score = round.player0Score !== null ? round.player0Score : (round.isInProgress ? 0 : '-');
    const p1Score = round.player1Score !== null ? round.player1Score : (round.isInProgress ? 0 : '-');

    const rowClass = round.isInProgress ? 'in-progress' : '';
    const p0Bust = bustByPlayer[0].has(round.round);
    const p1Bust = bustByPlayer[1].has(round.round);
    const p0CellClass = `score-cell${p0Bust ? ' bust' : ''}`;
    const p1CellClass = `score-cell${p1Bust ? ' bust' : ''}`;
    const p0Indicator = p0Bust ? '<span class="bust-indicator">*</span>' : '';
    const p1Indicator = p1Bust ? '<span class="bust-indicator">*</span>' : '';

    tableHTML += `
      <tr class="${rowClass}">
        <td class="round-label">${label}</td>
        <td class="${p0CellClass}">${p0Score}${p0Indicator}</td>
        <td class="${p1CellClass}">${p1Score}${p1Indicator}</td>
      </tr>
    `;
  });

  // Add totals row
  const inSuddenDeath = phase === 'sudden_death';
  const p0Total = currentScores[0];
  const p1Total = currentScores[1];

  const createBustTotal = (playerIndex: 0 | 1): string => {
    const bustCount = bustCounts[playerIndex];
    if (!bustCount) return currentScores[playerIndex].toString();
    const crosses = Array.from({ length: bustCount }, () => '<span class="crossed-out">21</span>').join('');
    return `
      <div class="bust-total">
        ${crosses}
        <span class="bust-to">${currentScores[playerIndex]}</span>
      </div>
    `;
  };

  let p0TotalDisplay = createBustTotal(0);
  let p1TotalDisplay = createBustTotal(1);

  // If in sudden death, show crossed out 21 with SD total
  if (inSuddenDeath) {
    const p0SD = turnScoreHistory
      .filter(t => t.player === 0 && t.suddenDeathRound !== undefined)
      .reduce((sum, t) => sum + t.points, 0);
    const p1SD = turnScoreHistory
      .filter(t => t.player === 1 && t.suddenDeathRound !== undefined)
      .reduce((sum, t) => sum + t.points, 0);

    p0TotalDisplay = `<span class="crossed-out">21</span> ${p0SD}`;
    p1TotalDisplay = `<span class="crossed-out">21</span> ${p1SD}`;
  }

  tableHTML += `
      </tbody>
      <tfoot>
        <tr class="totals-row">
          <td class="round-label">Tot</td>
          <td class="score-cell total">${p0TotalDisplay}</td>
          <td class="score-cell total">${p1TotalDisplay}</td>
        </tr>
      </tfoot>
    </table>
  `;

  content.innerHTML = tableHTML;
  return content;
}
